// Countdown — variant A "platform-timetable".
//
// Shows the time remaining until /config/event.startsAt. The countdown swells
// in three phases so the room feels different at minute T-60 vs T-10 vs T-0:
//   > 1hr      basic, BreathDot still
//   ≤ 1hr      BreathDot ripples, digits font-weight ramps 400→700
//   ≤ 10min    label "還  有" flips to "快  了", FogLayer opacity bumps, hairline contracts
// Once `now ≥ startsAt`, AuthGate stops rendering Countdown → RootRedirect → /home.
//
// Push permission has a 4th state 'unsupported' so iOS-Safari-tab learners see
// "請先將 app 加到主畫面" instead of "denied".

import {
  BreathDot,
  FogLayer,
  Hairline,
  Han,
  MonoData,
  PlatformEdge,
  RailLayer,
  T,
  TextBtn,
  useReducedMotion,
} from "@goodbye/ui";
import { userDoc, useFirestoreDoc } from "@goodbye/firebase";
import { useEffect, useMemo, useState } from "react";
import { NotificationPrompt } from "../components/NotificationPrompt";
import { useAppAuth } from "../context";
import { logoutLearner } from "../hooks/useNameAuth";
import { usePushPermission } from "../hooks/usePushPermission";
import type { PushPermission } from "../hooks/usePushPermission";
import {
  hasFreshPushSubscription,
  subscribePushNow,
  usePushSubscribe,
} from "../hooks/usePushSubscribe";

const HOUR_MS = 60 * 60 * 1000;
const TEN_MIN_MS = 10 * 60 * 1000;

function pad(n: number): string {
  return n < 10 ? "0" + n : String(n);
}

function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

type Phase = "far" | "near" | "imminent";

function pickPhase(remainingMs: number): Phase {
  if (remainingMs <= TEN_MIN_MS) return "imminent";
  if (remainingMs <= HOUR_MS) return "near";
  return "far";
}

export interface CountdownProps {
  startsAtMs: number;
}

export function Countdown({ startsAtMs }: CountdownProps) {
  const { userId } = useAppAuth();
  const ref = useMemo(() => userDoc(userId), [userId]);
  const { data: me } = useFirestoreDoc(ref);
  const { permission, request } = usePushPermission();
  usePushSubscribe(userId, permission);

  // 2026-05-28 — Countdown 也彈 NotificationPrompt modal,跟 Home 同一條
  // 流程,避免「提早打開的學員停在倒數頁、沒走完授權、活動開始才看到 modal」。
  // 邏輯跟 Home.tsx 對齊:permission=default 或 (granted 但 !isFresh) 就彈、
  // 不依賴 localStorage close、subscribePushNow 成功直接關 modal。
  const [isFresh, setIsFresh] = useState<boolean>(() =>
    hasFreshPushSubscription(),
  );
  useEffect(() => {
    if (isFresh) return;
    if (permission !== "granted") return;
    const id = window.setInterval(() => {
      if (hasFreshPushSubscription()) {
        setIsFresh(true);
        window.clearInterval(id);
      }
    }, 500);
    return () => window.clearInterval(id);
  }, [isFresh, permission]);

  const [notifPromptDismissed, setNotifPromptDismissed] = useState(false);
  const showNotifPrompt =
    !notifPromptDismissed &&
    !isFresh &&
    (permission === "default" || permission === "granted");

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const remaining = Math.max(0, startsAtMs - now);
  const phase = pickPhase(remaining);
  const reducedMotion = useReducedMotion();

  const digitsWeight = phase === "near" ? 600 : phase === "imminent" ? 700 : 400;
  const fogOpacity = phase === "imminent" ? 0.6 : phase === "near" ? 0.5 : 0.4;
  const labelText = phase === "imminent" ? "靠  近" : "距  離";

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: "100dvh",
        position: "relative",
        background: T.paper,
        overflow: "hidden",
      }}
    >
      <FogLayer opacity={fogOpacity} />
      <RailLayer side="right" opacity={0.5} />
      <PlatformEdge bottom={108} opacity={0.4} />

      {/* HUD top-right */}
      <div
        style={{
          position: "absolute",
          top: 18,
          right: 24,
          zIndex: 2,
        }}
      >
        <MonoData size={11} color={T.ink500} style={{ letterSpacing: "0.2em" }}>
          ↑ 北
        </MonoData>
      </div>

      {/* Brand */}
      <div style={{ padding: "calc(54px + env(safe-area-inset-top, 0px)) 32px 0", position: "relative", zIndex: 1 }}>
        <Han size={13} color={T.ink500} ls="0.3em">
          再見。再見
        </Han>
      </div>

      {/* Center stack */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 36,
          padding: "0 32px",
        }}
      >
        <Han size={14} color={T.ink500} ls="0.4em">
          {labelText}
        </Han>
        <span
          style={{
            fontFamily: T.mono,
            fontSize: 48,
            fontWeight: digitsWeight,
            letterSpacing: "0.08em",
            color: T.ink900,
            lineHeight: 1,
            fontVariantNumeric: "tabular-nums",
            transition: reducedMotion ? "none" : "font-weight 800ms var(--ease-mist)",
          }}
        >
          {formatRemaining(remaining)}
        </span>
        <BreathDot ripple={phase !== "far"} />
      </div>

      {/* Bottom: name + hairline + helper */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          padding: "0 32px 32px",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <Han size={13} color={T.ink500}>
            你的名字是
          </Han>
          <Han size={20} color={T.ink900} ls="0.05em">
            {me?.name ?? userId}
          </Han>
        </div>
        <Hairline
          style={{
            width: phase === "imminent" ? 28 : "100%",
            transition: reducedMotion ? "none" : "width 1200ms var(--ease-mist)",
          }}
        />
        <Han size={12} color={T.ink350} lh={1.9}>
          請保持手機通知開啟、<br />
          將 app 加到主畫面。
        </Han>
        <PushHint permission={permission} onRequest={request} />

        {/* Day Final: escape routes — learner can re-login, worker can
            preview. Both small mono links, low visual weight, no frames. */}
        <div
          style={{
            display: "flex",
            gap: 16,
            marginTop: 12,
            flexWrap: "wrap",
          }}
        >
          <TextBtn
            onClick={logoutLearner}
            mono
            size={10}
            ls="0.2em"
            color={T.ink350}
            framed={false}
            style={{ padding: "4px 0", minHeight: 0, minWidth: 0 }}
          >
            · 不是我?重新登入
          </TextBtn>
        </div>
      </div>

      {showNotifPrompt && (
        <NotificationPrompt
          permission={permission}
          onEnable={async () => {
            // 跟 Home.tsx 同一條 race-safe 路徑:
            // 1. 用 request() 回傳值判斷 permission (不依賴 React state)
            // 2. subscribePushNow 第一次 race fail 自動 retry 2 次
            // 3. 不 poll localStorage flag (iOS standalone 偶爾 silent fail)
            let effective: PushPermission = permission;
            if (permission === "default") {
              effective = await request();
            }
            if (effective !== "granted") return;
            if (!userId) return;
            let ok = await subscribePushNow(userId);
            if (!ok) {
              await new Promise((r) => setTimeout(r, 250));
              ok = await subscribePushNow(userId);
            }
            if (!ok) {
              await new Promise((r) => setTimeout(r, 500));
              ok = await subscribePushNow(userId);
            }
            if (ok) {
              setIsFresh(true);
              setNotifPromptDismissed(true);
            }
          }}
          onDismiss={() => setNotifPromptDismissed(true)}
        />
      )}
    </div>
  );
}

function PushHint({
  permission,
  onRequest,
}: {
  permission: ReturnType<typeof usePushPermission>["permission"];
  onRequest: () => Promise<unknown>;
}) {
  if (permission === "granted") {
    return (
      <MonoData size={11} color={T.ink350} style={{ letterSpacing: "0.2em" }}>
        · 通知已授權
      </MonoData>
    );
  }
  if (permission === "denied") {
    return (
      <MonoData size={11} color={T.ink700} style={{ letterSpacing: "0.2em" }}>
        · 通知被拒　請手動開啟
      </MonoData>
    );
  }
  if (permission === "unsupported") {
    return (
      <MonoData size={11} color={T.ink500} style={{ letterSpacing: "0.2em" }}>
        · 請先將 app 加到主畫面
      </MonoData>
    );
  }
  return (
    <TextBtn
      onClick={() => {
        onRequest();
      }}
      mono
      size={11}
      ls="0.2em"
      color={T.ink700}
      framed={false}
      style={{ alignSelf: "flex-start", padding: 0, minHeight: 0, minWidth: 0 }}
    >
      · 點此授權通知
    </TextBtn>
  );
}
